import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

function CandidateDetail() {
  const { id } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSub, setExpandedSub] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const navigate = useNavigate();
  const { admin, token, logout, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && !admin) navigate('/admin/login');
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    const fetchCandidate = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`/api/candidates/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCandidate(res.data.candidate || res.data);
        setSubmissions(res.data.submissions || []);
      } catch (err) {
        setError('Failed to load candidate details');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchCandidate();
  }, [id, token]);

  const parseCheatFlag = (flag) => {
    const tabMatch = flag.match(/tab_switch_during_Q(\d+)_at_(.+)/);
    const pasteMatch = flag.match(/paste_attempt_during_Q(\d+)_at_(.+)/);
    const oldTabMatch = flag.match(/tab_switch_at_(.+)/);
    const oldPasteMatch = flag.match(/paste_attempt_at_(.+)/);
    if (tabMatch) return { type: 'Tab Switch', question: parseInt(tabMatch[1]), time: new Date(tabMatch[2]) };
    if (pasteMatch) return { type: 'Paste Attempt', question: parseInt(pasteMatch[1]), time: new Date(pasteMatch[2]) };
    if (oldTabMatch) return { type: 'Tab Switch', question: null, time: new Date(oldTabMatch[1]) };
    if (oldPasteMatch) return { type: 'Paste Attempt', question: null, time: new Date(oldPasteMatch[1]) };
    return { type: flag, question: null, time: null };
  };

  if (authLoading || loading) {
    return (
      <div className="admin-layout">
        <aside className="sidebar"><div className="sidebar-header"><h2>Exam System</h2></div></aside>
        <main className="admin-main"><div className="loading-spinner">Loading...</div></main>
      </div>
    );
  }

  const profile = candidate?.profileData || {};
  const skills = candidate?.skills?.length > 0 ? candidate.skills : (profile.skills || []);
  const skillCategories = Array.isArray(profile.skillCategories) ? profile.skillCategories : [];

  // Parse experience: use structured array if available, otherwise parse raw string
  const experienceEntries = (() => {
    if (Array.isArray(profile.experience) && profile.experience.length > 0) return profile.experience;
    const raw = candidate?.experience || (typeof profile.experience === 'string' ? profile.experience : '');
    if (!raw) return [];
    const lines = raw.split('\n').map(l => l.trim()).filter(l => l);
    const entries = [];
    let current = null;
    const dateRe = /\b(\d{2}\/\d{4}|\d{4})\s*[-–—]\s*(present|\d{2}\/\d{4}|\d{4}|current|now)\b/i;
    const skipRe = /^(key responsibilit|responsibilities)/i;

    for (const line of lines) {
      const isBullet = /^[•·●\-\*▪]/.test(line);
      const hasDate = dateRe.test(line);
      const isShort = line.length < 100 && !isBullet;

      if (hasDate && current && !current.duration) {
        // Date line for an existing entry that has no duration yet
        const dm = line.match(dateRe);
        current.duration = dm ? dm[0].trim() : line;
      } else if (hasDate && !isBullet) {
        // New entry starting with a date
        if (current) entries.push(current);
        const dm = line.match(dateRe);
        let title = line.substring(0, line.indexOf(dm[0])).replace(/[|,\-–—]+$/, '').trim();
        current = { title, company: '', duration: dm[0].trim(), bullets: [] };
      } else if (!current && isShort && !isBullet) {
        // First short line = title of a new entry
        if (current) entries.push(current);
        current = { title: line, company: '', duration: '', bullets: [] };
      } else if (current && skipRe.test(line)) {
        // Skip "Key responsibilities:" header
      } else if (current && !current.company && isShort && !isBullet && current.bullets.length === 0) {
        // Short line before any bullets = company name
        current.company = line;
      } else if (current) {
        const clean = line.replace(/^[•·●\-\*▪]\s*/, '').trim();
        if (clean) current.bullets.push(clean);
      }
    }
    if (current) entries.push(current);
    return entries;
  })();

  // Parse education: use structured array if available, otherwise parse raw string
  const educationEntries = (() => {
    if (Array.isArray(profile.education) && profile.education.length > 0) return profile.education;
    const raw = candidate?.education || (typeof profile.education === 'string' ? profile.education : '');
    if (!raw) return [];
    const lines = raw.split('\n').map(l => l.trim()).filter(l => l);
    const entries = [];
    let current = null;
    const degreeRe = /\b(b\.?tech|b\.?sc|b\.?e\b|b\.?a\b|m\.?tech|m\.?sc|m\.?s\b|m\.?b\.?a|m\.?a\b|ph\.?d|diploma|bachelor|master|ssc|hsc|10th|12th|10\+2|b\.?c\.?a|m\.?c\.?a|b\.?com|m\.?com|certificate)\b/i;
    const yearRe = /\b(19|20)\d{2}\b/g;

    for (const line of lines) {
      const isBullet = /^[•·●\-\*▪]/.test(line);
      // Stop at non-education sections
      if (/^(technical skills|soft skills|skills|tools|certifications|projects|hobbies|languages|cloud|scm|build|artifact|ci|container|infrastructure|monitoring|networking|code coverage|scripting|configuration|operating)/i.test(line)) break;

      const hasDegree = degreeRe.test(line);
      const isShort = line.length < 120 && !isBullet;
      const isInstitution = /\b(university|college|institute|school|academy|polytechnic|highschool)\b/i.test(line);

      if (current && !current.institution && isShort && !isBullet && (isInstitution || !hasDegree)) {
        // Institution line for the current degree (may contain year)
        const years = line.match(yearRe);
        if (years) {
          current.year = years.length >= 2 ? `${years[0]} - ${years[years.length - 1]}` : years[0];
        }
        let inst = line;
        if (years) years.forEach(y => { inst = inst.replace(y, ''); });
        inst = inst.replace(/[|,\-–—]+/g, ' ').replace(/\s+/g, ' ').trim();
        current.institution = inst;
      } else if ((hasDegree || (isShort && !isBullet && !current)) && !isBullet) {
        if (current) entries.push(current);
        current = { degree: line, institution: '', year: '', details: '' };
      } else if (current) {
        const clean = line.replace(/^[•·●\-\*▪]\s*/, '').trim();
        if (clean) {
          // Check if this is a year/location line
          const years = clean.match(yearRe);
          if (years && !current.year) {
            current.year = years.length >= 2 ? `${years[0]} - ${years[years.length - 1]}` : years[0];
          } else {
            current.details += (current.details ? '; ' : '') + clean;
          }
        }
      }
    }
    if (current) entries.push(current);
    return entries;
  })();

  // Build skill categories from flat skills array if no categories exist
  const displaySkillCategories = (() => {
    if (skillCategories.length > 0) return skillCategories;
    if (skills.length === 0) return [];
    const catMap = {};
    skills.forEach(s => {
      const m = s.match(/^(.+?)\s*[:]\s*(.+)$/);
      if (m) {
        const cat = m[1].trim();
        if (!catMap[cat]) catMap[cat] = [];
        m[2].split(/[,;]/).map(v => v.trim()).filter(Boolean).forEach(v => catMap[cat].push(v));
      } else {
        if (!catMap['General']) catMap['General'] = [];
        catMap['General'].push(s);
      }
    });
    return Object.entries(catMap).map(([name, items]) => ({ name, items }));
  })();

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-header"><h2>Exam System</h2></div>
        <nav className="sidebar-nav">
          <Link to="/admin/dashboard" className="nav-link">Dashboard</Link>
          <Link to="/admin/create-test" className="nav-link">Create Test</Link>
          <Link to="/admin/candidates" className="nav-link active">Candidates</Link>
        </nav>
        <div className="sidebar-footer">
          <div className="admin-info"><span className="admin-name">{admin?.name || admin?.email}</span></div>
          <button className="btn btn-logout" onClick={logout}>Logout</button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-header">
          <div>
            <button className="back-link" onClick={() => navigate(-1)}>&larr; Back</button>
            <h1>Candidate Profile</h1>
          </div>
          {candidate?.cvPath && (
            <a href={`/uploads/${candidate.cvPath}?token=${token}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Download CV
            </a>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {candidate && (
          <>
            {/* Profile Header */}
            <div className="profile-header-card">
              <div className="profile-header-top">
                <div className="profile-avatar-lg">
                  {(candidate.name || candidate.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="profile-header-info">
                  <h2>{candidate.name || profile.name || 'Unnamed Candidate'}</h2>
                  <p className="profile-header-email">{candidate.email}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="tab-switch mb-1" style={{ marginTop: '1rem' }}>
              <button className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
                Profile Details
              </button>
              <button className={`tab-btn ${activeTab === 'exams' ? 'active' : ''}`} onClick={() => setActiveTab('exams')}>
                Exam Results ({submissions.length})
              </button>
            </div>

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="detail-sections">
                {/* Personal Information */}
                <div className="detail-card">
                  <h3>Personal Information</h3>
                  <table className="profile-table">
                    <tbody>
                      <tr>
                        <td className="profile-table-label">Full Name</td>
                        <td className="profile-table-value">{candidate.name || profile.name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="profile-table-label">Email</td>
                        <td className="profile-table-value">{candidate.email || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="profile-table-label">Phone</td>
                        <td className="profile-table-value">{candidate.phone || profile.phone || 'N/A'}</td>
                      </tr>
                      {profile.summary && (
                        <tr>
                          <td className="profile-table-label">Summary</td>
                          <td className="profile-table-value">{profile.summary}</td>
                        </tr>
                      )}
                      {candidate.currentCTC && (
                        <tr>
                          <td className="profile-table-label">Current CTC</td>
                          <td className="profile-table-value">{candidate.currentCTC}</td>
                        </tr>
                      )}
                      {candidate.totalExperience && (
                        <tr>
                          <td className="profile-table-label">Total Experience</td>
                          <td className="profile-table-value">{candidate.totalExperience}</td>
                        </tr>
                      )}
                      {candidate.noticePeriod && (
                        <tr>
                          <td className="profile-table-label">Notice Period</td>
                          <td className="profile-table-value">{candidate.noticePeriod}</td>
                        </tr>
                      )}
                      {candidate.expectedCTC && (
                        <tr>
                          <td className="profile-table-label">Expected CTC</td>
                          <td className="profile-table-value">{candidate.expectedCTC}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="profile-table-label">Registered</td>
                        <td className="profile-table-value">
                          {candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td className="profile-table-label">Resume</td>
                        <td className="profile-table-value">
                          {candidate.cvPath
                            ? <a href={`/uploads/${candidate.cvPath}?token=${token}`} target="_blank" rel="noopener noreferrer">View / Download CV</a>
                            : 'Not uploaded'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Skills & Tools — categorized */}
                <div className="detail-card">
                  <h3>Skills &amp; Technologies</h3>
                  {displaySkillCategories.length > 0 ? (
                    <table className="profile-table">
                      <thead>
                        <tr>
                          <th style={{ width: '180px' }}>Category</th>
                          <th>Technologies</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displaySkillCategories.map((cat, i) => (
                          <tr key={i}>
                            <td className="profile-table-label">{cat.name}</td>
                            <td>
                              <div className="skills-list">
                                {cat.items.map((s, j) => (
                                  <span key={j} className="skill-tag">{s}</span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : skills.length > 0 ? (
                    <table className="profile-table">
                      <thead>
                        <tr>
                          <th style={{ width: '180px' }}>Category</th>
                          <th>Technologies</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="profile-table-label">All Skills</td>
                          <td>
                            <div className="skills-list">
                              {skills.map((s, j) => (
                                <span key={j} className="skill-tag">{s}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted">No skills data extracted from CV.</p>
                  )}
                </div>

                {/* Experience — structured table */}
                <div className="detail-card">
                  <h3>Work Experience</h3>
                  {experienceEntries.length > 0 ? (
                    <table className="profile-table">
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Role / Title</th>
                          <th>Company</th>
                          <th style={{ width: '160px' }}>Duration</th>
                          <th>Key Responsibilities</th>
                        </tr>
                      </thead>
                      <tbody>
                        {experienceEntries.map((exp, i) => (
                          <tr key={i}>
                            <td className="profile-table-label">{i + 1}</td>
                            <td className="profile-table-value"><strong>{exp.title || 'N/A'}</strong></td>
                            <td className="profile-table-value">{exp.company || 'N/A'}</td>
                            <td className="profile-table-value">{exp.duration || 'N/A'}</td>
                            <td className="profile-table-value">
                              {exp.bullets && exp.bullets.length > 0 ? (
                                <ul className="profile-bullets">
                                  {exp.bullets.slice(0, 5).map((b, j) => <li key={j}>{b}</li>)}
                                  {exp.bullets.length > 5 && <li className="text-muted">+{exp.bullets.length - 5} more</li>}
                                </ul>
                              ) : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : candidate.experience ? (
                    <div className="profile-text-section">
                      {candidate.experience.split('\n').filter(l => l.trim()).map((line, i) => (
                        <p key={i} className="profile-text-line">{line.trim()}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No experience data extracted from CV.</p>
                  )}
                </div>

                {/* Education — structured table */}
                <div className="detail-card">
                  <h3>Education</h3>
                  {educationEntries.length > 0 ? (
                    <table className="profile-table">
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Degree / Qualification</th>
                          <th>Institution</th>
                          <th style={{ width: '120px' }}>Year</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {educationEntries.map((edu, i) => (
                          <tr key={i}>
                            <td className="profile-table-label">{i + 1}</td>
                            <td className="profile-table-value"><strong>{edu.degree || 'N/A'}</strong></td>
                            <td className="profile-table-value">{edu.institution || 'N/A'}</td>
                            <td className="profile-table-value">{edu.year || 'N/A'}</td>
                            <td className="profile-table-value">{edu.details || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : candidate.education ? (
                    <div className="profile-text-section">
                      {candidate.education.split('\n').filter(l => l.trim()).map((line, i) => (
                        <p key={i} className="profile-text-line">{line.trim()}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No education data extracted from CV.</p>
                  )}
                </div>

                {/* Exam Summary */}
                {submissions.length > 0 && (
                  <div className="detail-card">
                    <h3>Exam Summary</h3>
                    <table className="profile-table">
                      <thead>
                        <tr>
                          <th>Test</th>
                          <th>Score</th>
                          <th>Percentage</th>
                          <th>Status</th>
                          <th>Integrity</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.map(sub => {
                          const flags = sub.cheatFlags || [];
                          return (
                            <tr key={sub._id}>
                              <td className="profile-table-value">{sub.test?.title || 'N/A'}</td>
                              <td className="profile-table-value">{sub.scoringStatus === 'completed' ? `${Number(sub.totalScore || 0).toFixed(1)} / ${sub.maxScore || 0}` : '...'}</td>
                              <td className="profile-table-value">{sub.scoringStatus === 'completed' ? `${Math.round(sub.percentage || 0)}%` : '...'}</td>
                              <td>
                                {sub.scoringStatus === 'completed' ? (
                                  <span className={`status-badge ${sub.passed ? 'status-pass' : 'status-fail'}`}>
                                    {sub.passed ? 'Passed' : 'Failed'}
                                  </span>
                                ) : (
                                  <span className="status-badge" style={{ background: '#f59e0b', color: '#fff' }}>
                                    {sub.scoringStatus === 'failed' ? 'Scoring Failed' : 'Scoring...'}
                                  </span>
                                )}
                              </td>
                              <td>
                                {flags.length > 0
                                  ? <span className="cheat-flag-count">{flags.length} flags</span>
                                  : <span className="no-flags">Clean</span>}
                              </td>
                              <td className="profile-table-value">
                                {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Exams Tab */}
            {activeTab === 'exams' && (
              <div className="detail-sections">
                {submissions.length === 0 ? (
                  <div className="detail-card">
                    <p className="text-muted">No exams taken yet.</p>
                  </div>
                ) : (
                  submissions.map((sub) => {
                    const isExpanded = expandedSub === sub._id;
                    const flags = (sub.cheatFlags || []).map(parseCheatFlag);
                    const tabSwitches = flags.filter(f => f.type === 'Tab Switch');
                    const pasteAttempts = flags.filter(f => f.type === 'Paste Attempt');

                    return (
                      <div key={sub._id} className={`submission-card ${isExpanded ? 'expanded' : ''}`}>
                        <div className="submission-summary" onClick={() => setExpandedSub(isExpanded ? null : sub._id)}>
                          <div className="submission-test-name">
                            <strong>{sub.test?.title || 'Unknown Test'}</strong>
                            <span className="submission-date">
                              {sub.submittedAt ? new Date(sub.submittedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' IST' : ''}
                            </span>
                          </div>
                          <div className="submission-stats-row">
                            <div className="sub-stat">
                              <span className="sub-stat-label">Score</span>
                              <span className="sub-stat-value">{Number(sub.totalScore || 0).toFixed(1)} / {sub.maxScore || 0}</span>
                            </div>
                            <div className="sub-stat">
                              <span className="sub-stat-label">Percentage</span>
                              <span className={`sub-stat-value ${sub.passed ? 'text-pass' : 'text-fail'}`}>{sub.scoringStatus === 'completed' ? `${Math.round(sub.percentage || 0)}%` : '...'}</span>
                            </div>
                            <div className="sub-stat">
                              <span className="sub-stat-label">Status</span>
                              {sub.scoringStatus === 'completed' ? (
                                <span className={`status-badge ${sub.passed ? 'status-pass' : 'status-fail'}`}>{sub.passed ? 'Passed' : 'Failed'}</span>
                              ) : (
                                <span className="status-badge" style={{ background: '#f59e0b', color: '#fff' }}>{sub.scoringStatus === 'failed' ? 'Scoring Failed' : 'Scoring...'}</span>
                              )}
                            </div>
                            <div className="sub-stat">
                              <span className="sub-stat-label">Integrity</span>
                              <span className={`sub-stat-value ${flags.length > 0 ? 'text-fail' : 'text-pass'}`}>
                                {flags.length > 0 ? `${flags.length} flags` : 'Clean'}
                              </span>
                            </div>
                            <span className="expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="submission-details">
                            {/* Integrity Report */}
                            {flags.length > 0 && (
                              <div className="integrity-section">
                                <h4>Integrity Report</h4>
                                <div className="integrity-summary">
                                  <div className="integrity-stat">
                                    <span className="integrity-count">{tabSwitches.length}</span>
                                    <span className="integrity-label">Tab Switches</span>
                                  </div>
                                  <div className="integrity-stat">
                                    <span className="integrity-count">{pasteAttempts.length}</span>
                                    <span className="integrity-label">Paste Attempts</span>
                                  </div>
                                </div>
                                <table className="profile-table">
                                  <thead>
                                    <tr>
                                      <th style={{ width: '40px' }}>#</th>
                                      <th>Event Type</th>
                                      <th>Question</th>
                                      <th>Time</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {flags.map((flag, i) => (
                                      <tr key={i} className={flag.type === 'Tab Switch' ? 'row-danger' : 'row-warning'}>
                                        <td>{i + 1}</td>
                                        <td><strong>{flag.type}</strong></td>
                                        <td>{flag.question ? `Question ${flag.question}` : 'N/A'}</td>
                                        <td>{flag.time && !isNaN(flag.time) ? flag.time.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) + ' IST' : 'N/A'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Per-Question Answers */}
                            {sub.answers && sub.answers.length > 0 && (
                              <div className="answers-section">
                                <h4>Question-wise Answers &amp; Scores</h4>
                                {sub.answers.map((ans, i) => {
                                  const scorePercent = Math.round((ans.similarityScore || 0) * 100);
                                  const flagsForQ = flags.filter(f => f.question === ans.questionIndex + 1);
                                  return (
                                    <div key={i} className="detail-card" style={{ marginBottom: '1rem', border: '1px solid #e0e0e0' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                        <strong>Q{ans.questionIndex + 1}. {ans.questionText || 'Question not available'}</strong>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <div className="answer-score-bar-wrapper" style={{ height: '6px', width: '60px' }}>
                                            <div className="answer-score-bar" style={{
                                              width: `${scorePercent}%`,
                                              background: scorePercent >= 60 ? '#27ae60' : scorePercent >= 30 ? '#f39c12' : '#e74c3c'
                                            }} />
                                          </div>
                                          <span style={{ fontWeight: 'bold' }} className={scorePercent >= 60 ? 'text-pass' : 'text-fail'}>{scorePercent}%</span>
                                          {flagsForQ.length > 0 && <span className="answer-flag-indicator">&#9888; {flagsForQ.length}</span>}
                                        </div>
                                      </div>

                                      <div style={{ marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#555', textTransform: 'uppercase' }}>Candidate's Answer</span>
                                        <div style={{ background: '#f8f9fa', padding: '0.75rem', borderRadius: '6px', marginTop: '0.25rem', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                                          {ans.answer || <span className="text-muted">No answer provided</span>}
                                        </div>
                                      </div>

                                      <div style={{ marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#555', textTransform: 'uppercase' }}>Expected Answer</span>
                                        <div style={{ background: '#f0fdf4', padding: '0.75rem', borderRadius: '6px', marginTop: '0.25rem', whiteSpace: 'pre-wrap', fontSize: '0.9rem', borderLeft: '3px solid #27ae60' }}>
                                          {ans.expectedAnswer || <span className="text-muted">Not available</span>}
                                        </div>
                                      </div>

                                      {(ans.aiReason || (ans.aiMissing && ans.aiMissing.length > 0)) && (
                                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#555' }}>
                                          {ans.aiReason && (
                                            <div><strong>AI Reason:</strong> {ans.aiReason}</div>
                                          )}
                                          {ans.aiMissing && ans.aiMissing.length > 0 && (
                                            <div><strong>Missing:</strong> {ans.aiMissing.join(', ')}</div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default CandidateDetail;
